import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { FileText, Eye, Download, Calendar, DollarSign, Clock, CheckCircle } from 'lucide-react';
import * as api from '../lib/api';

const MOCK_CONTRACTS = [
  { contract_id: '1', title: 'Senior AI Engineer - TechCorp AI', company_name: 'TechCorp AI', contract_type: 'fixed_price', amount: '$15,000', status: 'active', start_date: '2025-01-15', end_date: '2025-04-15', progress: 60 },
  { contract_id: '2', title: 'ML Consultant - DataMinds', company_name: 'DataMinds', contract_type: 'hourly', amount: '$120/hr', status: 'active', start_date: '2025-01-20', end_date: 'Ongoing', progress: 30 },
  { contract_id: '3', title: 'Backend Developer - StartupXYZ', company_name: 'StartupXYZ', contract_type: 'fixed_price', amount: '$8,000', status: 'completed', start_date: '2024-12-01', end_date: '2025-01-10', progress: 100 },
];

const ContractsPage = () => {
  const { user } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, completed: 0, total_earned: '$0' });
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadContracts(); }, [user]);

  const loadContracts = async () => {
    try {
      const data = await api.getContracts();
      setContracts(data.contracts || []);
      setStats(data.stats || { total: data.contracts?.length || 0, active: 0, completed: 0, total_earned: '$0' });
    } catch {
      setContracts(MOCK_CONTRACTS);
      setStats({ total: 5, active: 2, completed: 3, total_earned: '$45k' });
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen bg-[#F9FBF5]">
      <Sidebar />
      <div className="flex-1 ml-0 md:ml-[70px] pt-[60px] md:pt-0 pb-[60px] md:pb-0">
        <div className="max-w-5xl mx-auto px-8 py-12">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Contracts</h1>
            <p className="text-gray-600">Manage your active and past contracts</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="p-6"><div className="text-2xl font-bold text-gray-900 mb-1">{stats.total}</div><div className="text-sm text-gray-600">Total Contracts</div></Card>
            <Card className="p-6"><div className="text-2xl font-bold text-green-600 mb-1">{stats.active}</div><div className="text-sm text-gray-600">Active</div></Card>
            <Card className="p-6"><div className="text-2xl font-bold text-gray-900 mb-1">{stats.completed}</div><div className="text-sm text-gray-600">Completed</div></Card>
            <Card className="p-6"><div className="text-2xl font-bold text-green-600 mb-1">{stats.total_earned}</div><div className="text-sm text-gray-600">Total Earned</div></Card>
          </div>

          {loading ? <div className="text-center py-12 text-gray-500">Loading contracts...</div> : (
            <div className="space-y-6">
              {contracts.map((c) => (
                <Card key={c.contract_id} className={`p-6 ${c.status === 'active' ? 'border-l-4 border-l-[#0A3900]' : ''}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">{c.title}</h3>
                      <p className="text-gray-600">{c.company_name}</p>
                    </div>
                    <Badge className={c.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>{c.status === 'active' ? 'Active' : 'Completed'}</Badge>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div><span className="text-sm text-gray-500">Type</span><p className="font-medium">{c.contract_type === 'fixed_price' ? 'Fixed Price' : 'Hourly'}</p></div>
                    <div><span className="text-sm text-gray-500">Amount</span><p className="font-medium">{c.amount}</p></div>
                    <div><span className="text-sm text-gray-500">Start Date</span><p className="font-medium">{c.start_date}</p></div>
                    <div><span className="text-sm text-gray-500">End Date</span><p className="font-medium">{c.end_date}</p></div>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Progress</span><span className="text-sm font-medium">{c.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                    <div className={`h-2 rounded-full ${c.status === 'active' ? 'bg-[#0A3900]' : 'bg-green-600'}`} style={{ width: `${c.progress}%` }} />
                  </div>
                  <div className="flex gap-3">
                    <Button variant="outline" size="sm"><Eye className="w-4 h-4 mr-2" />View Details</Button>
                    <Button variant="outline" size="sm"><Download className="w-4 h-4 mr-2" />Download PDF</Button>
                    {c.status === 'active' && <Button size="sm" className="bg-[#0A3900] hover:bg-[#1A5C0A]"><Calendar className="w-4 h-4 mr-2" />Log Time</Button>}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractsPage;
